import { capturePrivateSession, assertPrivateSessionLifetime, getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CircleAlert, CircleCheck, Info, LoaderCircle, Lock, Phone } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { isStoreDeactivated, STORE_DEACTIVATED_PARAM } from "./storeDeactivated";
import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { changePrivateSession } from "@/shared/privateSessionChange";
import { AuthField } from "./login/AuthField";
import { AuthShell, LoginIllustration } from "./login/AuthShell";
import { rememberStore } from "./login/rememberedStore";
import { STORE_PROFILE_KEY } from "@/shared/useStoreLogo";
import { sessionQueryOptions, useSession } from "./useSession";
import { landingPathForSession } from "./landing";

/**
 * Phone + password, the same credentials the seller portal uses — drawn as the
 * redesign's login (claude.ai/design "Login", 2026-10-06).
 *
 * `?out=1` (set by sign-out) and `?saved=1` (set after the first-password
 * change) put a green note above the form saying what just happened.
 */
export function LoginPage() {
  const lifetime = useRef(capturePrivateSession()).current;
  const t = useTranslate();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [searchParams] = useSearchParams();
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const identity = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  // `true` = wrong phone or password; "store-deactivated" = 403
  // STORE_DEACTIVATED (the password was right, the store is switched off).
  const { data: failed } = useQuery<boolean | "store-deactivated">({
    queryKey: ["public-login-feedback", identity.generation],
    queryFn: () => false, enabled: false,
  });
  const [isSubmitting, setSubmitting] = useState(false);
  /*
   * Already signed in?
   *
   * Then this screen is a dead end: typing `/login` (or reaching it from a
   * bookmark, which on a shared till is how staff open the app) showed a form
   * that had nothing to do — filling it in with the SAME account just returned
   * to where the session already was. It bounces through `landingPathForSession`, the
   * one place that answers "which surface does this member belong on", rather
   * than to a constant that would land a cook on a floor grid that 403s. Read
   * through `useSession` — the same query, the same cache entry — so there is
   * no second fetch beside the one the guarded shells make.
   */
  const { data: session } = useSession();

  // Sent here mid-session because an admin deactivated the store (403
  // STORE_DEACTIVATED): end the still-valid cookie once, so the device does
  // not bounce between the app and this page, and say why below.
  const deactivatedNotice = searchParams.get(STORE_DEACTIVATED_PARAM) === "1";
  const signedOutRef = useRef(false);
  useEffect(() => {
    if (!deactivatedNotice || signedOutRef.current) return;
    signedOutRef.current = true;
    void changePrivateSession(queryClient, () =>
      apiFetch(endpoints.seller.logout, { method: "POST" }),
    ).catch(() => undefined);
  }, [deactivatedNotice, queryClient]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    assertPrivateSessionLifetime(lifetime);
    queryClient.removeQueries({ queryKey: ["public-login-feedback"] });
    const generation = lifetime.generation + 1;
    setSubmitting(true);
    try {
      await changePrivateSession(queryClient, () => apiFetch(endpoints.seller.login, {
        method: "POST",
        body: { phone, password },
      }));
      if (getPrivateSessionState().generation !== lifetime.generation + 1) return;
      // WHERE to land is a capability question, not a constant: a cook holds
      // no `RESTAURANT_POS_USE`, so the old hardcoded `/pos` opened a floor
      // grid whose every call 403s. The session is read through the SAME
      // query the guarded shells use (one cache entry, no second fetch), and
      // if that read fails we defer to `/:locale`, which owns the decision
      // and has its own loading and error states — never a guessed surface.
      try {
        const session = await queryClient.fetchQuery(sessionQueryOptions);
        // The corner of the next login on this device shows the restaurant —
        // its logo too, best-effort: a failed read must not hold up sign-in.
        const logoUrl = await queryClient
          .fetchQuery({
            queryKey: STORE_PROFILE_KEY,
            queryFn: () => apiFetch<{ logoUrl: string | null }>(endpoints.seller.store),
          })
          .then((store) => store.logoUrl ?? null)
          .catch(() => null);
        rememberStore({ name: session.seller.storeName, logoUrl });
        navigate(landingPathForSession(locale, session), { replace: true });
      } catch {
        navigate(`/${locale}`, { replace: true });
      }
    } catch (failure) {
      // Deliberately one message for both "unknown phone" and "wrong password":
      // saying which one was wrong tells an attacker which phone numbers are
      // registered staff. A deactivated store is the one 403 that is said
      // plainly: the credentials were right.
      if (getPrivateSessionState().generation === generation) {
        queryClient.setQueryData(
          ["public-login-feedback", generation],
          isStoreDeactivated(failure) ? "store-deactivated" : true,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (session) {
    return <Navigate to={landingPathForSession(locale, session)} replace />;
  }

  const busy = isSubmitting || identity.changing;
  const note = searchParams.get("saved") === "1"
    ? t((m) => m.auth.passwordSaved)
    : searchParams.get("out") === "1"
      ? t((m) => m.auth.loggedOut)
      : null;
  const Arrow = locale === "ar" ? ArrowLeft : ArrowRight;

  return (
    <AuthShell subtitleRef={subtitleRef}>
      {/* The carousel says hello on a wide screen; on a phone the drawing
          sits over the heading instead. */}
      <span className="auth-hero auth-narrow-only" style={{ marginInlineStart: -14 }}>
        <LoginIllustration id="welcome" size={132} />
      </span>
      <h1 className="auth-title">{t((m) => m.auth.welcomeBack)}</h1>
      <span ref={subtitleRef} className="auth-sub">
        {t((m) => m.auth.loginSub)}
      </span>
      {note && !failed ? (
        <div className="auth-note" data-tone="ok" role="status" data-testid="login-note">
          <CircleCheck size={16} strokeWidth={1.8} color="rgb(68,131,97)" aria-hidden />
          <span>{note}</span>
        </div>
      ) : null}
      <form onSubmit={onSubmit} className="auth-form">
        <AuthField
          label={t((m) => m.auth.mobileNumber)}
          icon={Phone}
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="username"
          testId="login-phone"
          dir="ltr"
          placeholder="01X XXXX XXXX"
          required
          invalid={Boolean(failed)}
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
        <AuthField
          label={t((m) => m.auth.password)}
          icon={Lock}
          name="password"
          type="password"
          autoComplete="current-password"
          testId="login-password"
          required
          invalid={Boolean(failed)}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {deactivatedNotice && !failed ? (
          <div className="auth-note" data-tone="error" role="alert" data-testid="login-deactivated">
            <CircleAlert size={16} strokeWidth={1.8} aria-hidden />
            <span>{t((m) => m.auth.storeDeactivated)}</span>
          </div>
        ) : null}
        {failed ? (
          <div className="auth-note" data-tone="error" role="alert">
            <CircleAlert size={16} strokeWidth={1.8} color="rgb(212,76,71)" aria-hidden />
            <span>
              {t((m) =>
                failed === "store-deactivated"
                  ? m.auth.storeDeactivated
                  : m.auth.failed,
              )}
            </span>
          </div>
        ) : null}
        <button type="submit" className="auth-submit" disabled={busy} data-testid="login-submit">
          {busy ? (
            <span className="auth-spin">
              <LoaderCircle size={16} strokeWidth={2} aria-hidden />
            </span>
          ) : null}
          <span>{t((m) => (busy ? m.auth.loggingIn : m.auth.submit))}</span>
          {busy ? null : <Arrow size={16} strokeWidth={2} aria-hidden />}
        </button>
      </form>
      <div className="auth-foot">
        <Info size={15} strokeWidth={1.8} aria-hidden />
        <span>{t((m) => m.auth.forgotPassword)}</span>
      </div>
    </AuthShell>
  );
}
